import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgClass } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, map, of } from 'rxjs';
import { FlashService } from '../../../../../services/flash';
import { TeacherService } from '../../../../../services/teacher';
import { Session } from '../../../../../services/session';
import { SubjectService } from '../../../../../services/subject';
import { AcademicTermService } from '../../../../../services/academic-term';
import { AssessmentService } from '../../../../../services/assessment';
import { ITeacher } from '../../../../../interfaces/teacher';
import { ITeacherClassSubjects } from '../../../../../interfaces/class';
import { IAssessmentConfigPayload } from '../../../../../interfaces/assessment';
import { ApiEndpoints } from '../../../../../core/api-endpoints';
import { Helpers } from '../../../../../utilities/helpers';
import { ShimmerEffect } from '../../../../../shared/shimmer-effect/shimmer-effect';
import { LoadingIndicator } from '../../../../../shared/loading-indicator/loading-indicator';
import { NotFoundError } from '../../../../../shared/not-found-error/not-found-error';

type TeacherClassSubject = ITeacherClassSubjects['payload'][number];

interface TeacherSubjectClass {
  class_id: string;
  class_name: string;
  academic_year_id: string;
  academic_year_name: string;
}

interface TeacherSubjectGroup {
  level_subject_id: string;
  subject_id: string;
  subject_name: string;
  subject_code: string;
  level_name: string;
  classes: TeacherSubjectClass[];
}

@Component({
  selector: 'app-teacher-detail',
  imports: [FormsModule, NgClass, ShimmerEffect, LoadingIndicator, NotFoundError],
  templateUrl: './teacher-detail.html',
  styleUrl: './teacher-detail.css',
})
export class TeacherDetail {
  constructor(
    private teacherService: TeacherService,
    private route: ActivatedRoute,
    private router: Router,
    private session: Session,
    private subjectService: SubjectService,
    private academicTermService: AcademicTermService,
    private assessmentService: AssessmentService,
  ) {
    this.route.queryParamMap.subscribe((params) => {
      const id = params.get('id');

      if (!id) {
        void this.router.navigate(['/dashboard/class']);
        return;
      }

      this.teacherId = id;
      this.teacherAssignments.set([]);
      this.subjectConfigs.set({});
      this.configErrors.set({});
      this.getTeacherById();
      this.getTeacherAssignments();
    });

    const token = this.session.getItem('token');

    this.loggedUserRole = this.session.decodeToken(token)?.role;
  }

  protected teacher = signal<ITeacher | null>(null);
  protected teacherAssignments = signal<TeacherClassSubject[]>([]);
  protected isLoadingTeacher = signal<boolean>(false);
  protected isLoadingSubjects = signal<boolean>(false);
  protected selectedSubject = signal<TeacherSubjectGroup | null>(null);
  protected subjectConfigs = signal<Record<string, IAssessmentConfigPayload | null>>({});
  protected configErrors = signal<Record<string, string>>({});
  protected isLoadingConfigs = signal<boolean>(false);
  protected assessmentConfig = signal<IAssessmentConfigPayload | null>(null);
  protected configError = signal<string>('');
  protected popupAction = signal<'finalize' | 'reopen'>('finalize');
  protected showConfirmationPopup = signal<boolean>(false);
  protected isProcessing = signal<boolean>(false);
  protected reopenReason = '';
  protected teacherId = '';
  protected loggedUserRole?: string;
  protected readonly Helpers = Helpers;
  protected readonly flashService = inject(FlashService);

  private readonly termCache = new Map<string, string>();

  protected readonly teacherName = computed(() => {
    const teacher = this.teacher()?.payload;

    if (!teacher) {
      return '';
    }

    return Helpers.fullName({
      first_name: teacher.first_name,
      last_name: teacher.last_name,
    });
  });

  protected readonly qualificationText = computed(() => {
    const qualifications = this.teacher()?.payload?.qualification;

    if (!Array.isArray(qualifications) || qualifications.length === 0) {
      return 'N/A';
    }

    return qualifications.join(', ');
  });

  protected readonly subjectGroups = computed<TeacherSubjectGroup[]>(() => {
    const groups = new Map<string, TeacherSubjectGroup>();

    for (const assignment of this.teacherAssignments()) {
      let group = groups.get(assignment.level_subject_id);

      if (!group) {
        group = {
          level_subject_id: assignment.level_subject_id,
          subject_id: assignment.subject_id,
          subject_name: assignment.subject_name,
          subject_code: assignment.subject_code,
          level_name: assignment.level_name,
          classes: [],
        };

        groups.set(assignment.level_subject_id, group);
      }

      const hasClass = group.classes.some((item) => item.class_id === assignment.class_id);

      if (!hasClass) {
        group.classes.push({
          class_id: assignment.class_id,
          class_name: assignment.class_name,
          academic_year_id: assignment.academic_year_id,
          academic_year_name: assignment.academic_year_name,
        });
      }
    }

    return Array.from(groups.values()).sort(
      (first, second) =>
        first.level_name.localeCompare(second.level_name) ||
        first.subject_name.localeCompare(second.subject_name),
    );
  });

  protected get isAdmin(): boolean {
    return ['SUPER_ADMIN', 'SCHOOL_ADMIN'].includes(this.loggedUserRole ?? '');
  }

  protected get canFinalize(): boolean {
    const config = this.assessmentConfig();

    return (
      this.isAdmin &&
      !!config &&
      config.status === 'ACTIVE' &&
      !this.isConfigFinalized(config) &&
      !this.isProcessing()
    );
  }

  protected get canReopen(): boolean {
    const config = this.assessmentConfig();

    return (
      this.isAdmin &&
      !!config &&
      this.isConfigFinalized(config) &&
      this.reopenReason.trim().length > 0 &&
      !this.isProcessing()
    );
  }

  protected getSubjectConfig(subject: TeacherSubjectGroup): IAssessmentConfigPayload | null {
    return this.subjectConfigs()[subject.level_subject_id] ?? null;
  }

  protected isSubjectFinalized(subject: TeacherSubjectGroup): boolean {
    return this.isConfigFinalized(this.getSubjectConfig(subject));
  }

  protected isSubjectConfigLoading(subject: TeacherSubjectGroup): boolean {
    return this.isLoadingConfigs() && !this.getSubjectConfig(subject);
  }

  private isConfigFinalized(config: IAssessmentConfigPayload | null): boolean {
    return !!config && config.is_finalized;
  }

  protected getTeacherById(): void {
    this.isLoadingTeacher.set(true);

    this.teacherService.getTeacherById(`${ApiEndpoints.url}/teacher/${this.teacherId}`).subscribe({
      next: (data: ITeacher) => {
        this.teacher.set(data);
        this.isLoadingTeacher.set(false);
      },
      error: (err) => {
        this.isLoadingTeacher.set(false);
        this.flashService.failure(err?.error?.message ?? 'Teacher not found');
      },
    });
  }

  protected getTeacherAssignments(): void {
    this.isLoadingSubjects.set(true);

    const params: Record<string, string | number> = {
      page: 0,
      size: 500,
      teacher_id: this.teacherId,
      status: 'ACTIVE',
    };

    this.subjectService
      .getTeacherClassSubjects(`${ApiEndpoints.url}/teacher-class-subject`, params)
      .subscribe({
        next: (res) => {
          this.teacherAssignments.set(res.payload ?? []);
          this.isLoadingSubjects.set(false);

          if (this.isAdmin) {
            this.loadSubjectConfigs();
          }
        },
        error: (err) => {
          this.isLoadingSubjects.set(false);
          this.flashService.failure(
            err?.error?.message ?? 'Could not load the subjects taught by this teacher.',
          );
        },
      });
  }

  protected openConfirmation(subject: TeacherSubjectGroup): void {
    if (!this.isAdmin) {
      return;
    }

    const config = this.getSubjectConfig(subject);

    if (!config && this.isSubjectConfigLoading(subject)) {
      this.flashService.failure('The assessment configuration is still loading. Try again.');
      return;
    }

    this.selectedSubject.set(subject);
    this.assessmentConfig.set(config);
    this.popupAction.set(this.isConfigFinalized(config) ? 'reopen' : 'finalize');
    this.configError.set(
      config
        ? ''
        : (this.configErrors()[subject.level_subject_id] ??
            'No assessment configuration is available for this subject in the current term.'),
    );
    this.reopenReason = '';
    this.showConfirmationPopup.set(true);
  }

  protected cancelConfirmation(): void {
    if (this.isProcessing()) {
      return;
    }

    this.showConfirmationPopup.set(false);
    this.assessmentConfig.set(null);
    this.configError.set('');
    this.reopenReason = '';
  }

  protected confirmAction(): void {
    if (this.popupAction() === 'reopen') {
      this.reopenAssessment();
      return;
    }

    this.finalizeAssessment();
  }

  private loadSubjectConfigs(): void {
    const groups = this.subjectGroups();

    if (groups.length === 0) {
      this.isLoadingConfigs.set(false);
      return;
    }

    for (const group of groups) {
      if (!group.classes[0]?.academic_year_id) {
        this.setSubjectConfigError(
          group.level_subject_id,
          'This subject is not linked to an academic year.',
        );
      }
    }

    const yearIds = Array.from(
      new Set(
        groups
          .map((group) => group.classes[0]?.academic_year_id)
          .filter((id): id is string => !!id),
      ),
    );

    if (yearIds.length === 0) {
      this.isLoadingConfigs.set(false);
      return;
    }

    this.isLoadingConfigs.set(true);

    let pending = yearIds.length;
    const done = () => {
      pending -= 1;

      if (pending <= 0) {
        this.isLoadingConfigs.set(false);
      }
    };

    for (const yearId of yearIds) {
      const yearGroups = groups.filter((group) => group.classes[0]?.academic_year_id === yearId);

      this.getCurrentTermId(yearId).subscribe({
        next: (termId) => {
          if (!termId) {
            for (const group of yearGroups) {
              this.setSubjectConfigError(
                group.level_subject_id,
                'No current academic term is configured for this academic year.',
              );
            }

            done();
            return;
          }

          const params: Record<string, string | number> = {
            page: 0,
            size: 500,
            academic_year_id: yearId,
            term_id: termId,
          };

          this.assessmentService
            .getAssessmentConfigs(`${ApiEndpoints.url}/assessment-config`, params)
            .subscribe({
              next: (res) => {
                const configs = res.payload ?? [];

                for (const group of yearGroups) {
                  const config =
                    configs.find((item) => item.level_subject_id === group.level_subject_id) ??
                    null;

                  this.setSubjectConfig(group.level_subject_id, config);

                  if (!config) {
                    this.setSubjectConfigError(
                      group.level_subject_id,
                      'No assessment configuration was found for this subject in the current term.',
                    );
                  }
                }

                done();
              },
              error: (err) => {
                const message =
                  err?.error?.message ?? 'Could not load the assessment configurations.';

                for (const group of yearGroups) {
                  this.setSubjectConfigError(group.level_subject_id, message);
                }

                done();
              },
            });
        },
        error: (err) => {
          const message = err?.error?.message ?? 'Could not load the current academic term.';

          for (const group of yearGroups) {
            this.setSubjectConfigError(group.level_subject_id, message);
          }

          done();
        },
      });
    }
  }

  private getCurrentTermId(academicYearId: string): Observable<string | null> {
    const cached = this.termCache.get(academicYearId);

    if (cached) {
      return of(cached);
    }

    const params: Record<string, string | number | boolean> = {
      page: 0,
      size: 1,
      current_term: true,
      academic_year_id: academicYearId,
    };

    return this.academicTermService
      .getAcademicTerms(`${ApiEndpoints.url}/academic-term`, params)
      .pipe(
        map((res) => {
          const termId = res.payload?.[0]?.id ?? null;

          if (termId) {
            this.termCache.set(academicYearId, termId);
          }

          return termId;
        }),
      );
  }

  private setSubjectConfig(levelSubjectId: string, config: IAssessmentConfigPayload | null): void {
    this.subjectConfigs.update((current) => ({ ...current, [levelSubjectId]: config }));
    this.configErrors.update((current) => {
      const next = { ...current };
      delete next[levelSubjectId];
      return next;
    });
  }

  private setSubjectConfigError(levelSubjectId: string, message: string): void {
    this.subjectConfigs.update((current) => ({ ...current, [levelSubjectId]: null }));
    this.configErrors.update((current) => ({ ...current, [levelSubjectId]: message }));
  }

  protected finalizeAssessment(): void {
    const config = this.assessmentConfig();

    if (!config || !this.canFinalize) {
      return;
    }

    this.isProcessing.set(true);

    this.assessmentService
      .finalizeAssessment(`${ApiEndpoints.url}/assessment-result/finalize`, {
        assessment_config_id: config.id,
      })
      .subscribe({
        next: (res) => {
          const status = res?.payload?.assessment_config?.status ?? 'LOCKED';
          const updatedConfig = { ...config, is_finalized: true, status };

          this.isProcessing.set(false);
          this.assessmentConfig.set(updatedConfig);
          this.setSubjectConfig(config.level_subject_id, updatedConfig);
          this.showConfirmationPopup.set(false);
          this.reopenReason = '';
          this.flashService.success('Assessment finalized successfully.');
        },
        error: (err) => {
          this.isProcessing.set(false);
          this.flashService.failure(
            err?.error?.message ?? 'Could not finalize the assessment. Try again.',
          );
        },
      });
  }

  protected reopenAssessment(): void {
    const config = this.assessmentConfig();
    const reason = this.reopenReason.trim();

    if (!config || !reason || !this.canReopen) {
      return;
    }

    this.isProcessing.set(true);

    this.assessmentService
      .reopenAssessment(`${ApiEndpoints.url}/assessment-result/reopen`, {
        assessment_config_id: config.id,
        reason,
      })
      .subscribe({
        next: (res) => {
          const status = res?.payload?.status ?? 'ACTIVE';
          const updatedConfig = { ...config, is_finalized: false, status };

          this.isProcessing.set(false);
          this.assessmentConfig.set(updatedConfig);
          this.setSubjectConfig(config.level_subject_id, updatedConfig);
          this.showConfirmationPopup.set(false);
          this.reopenReason = '';
          this.flashService.success('Assessment reopened successfully.');
        },
        error: (err) => {
          this.isProcessing.set(false);
          this.flashService.failure(
            err?.error?.message ?? 'Could not reopen the assessment. Try again.',
          );
        },
      });
  }
}
